import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
// import { Switch } from '@/components/ui/switch'; // Unused import
import { Plus, Edit, Calendar, Play, ChevronDown, ChevronRight } from 'lucide-react';
import { adminApi } from '@/lib/api';

interface ConsentPolicy {
  id: string;
  name: string;
  version: string;
  policyText: string;
  effectiveDate: string;
  policyHash: string;
  createdAt: string;
  updatedAt: string;
}

interface GroupedPolicy {
  name: string;
  activePolicy: ConsentPolicy;
  allVersions: ConsentPolicy[];
  isExpanded?: boolean;
}

interface PolicyFormData {
  name: string;
  policyText: string;
  effectiveDate: string;
  isActive: boolean;
}

export function ConsentPoliciesPage() {
  const [groupedPolicies, setGroupedPolicies] = useState<GroupedPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<ConsentPolicy | null>(null);
  const [activatingPolicy, setActivatingPolicy] = useState<string | null>(null);

  const [formData, setFormData] = useState<PolicyFormData>({
    name: '',
    policyText: '',
    effectiveDate: new Date().toISOString().split('T')[0] ?? '',
    isActive: true
  });

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const policies = await adminApi.getConsentPolicies();

      // Group policies by name and find active ones
      const grouped = groupPoliciesByName(policies);
      setGroupedPolicies(grouped);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const groupPoliciesByName = (policies: ConsentPolicy[]): GroupedPolicy[] => {
    const groups: { [name: string]: ConsentPolicy[] } = {};

    // Group by name
    policies.forEach(policy => {
      if (!groups[policy.name]) {
        groups[policy.name] = [];
      }
      groups[policy.name]!.push(policy);
    });

    // Convert to GroupedPolicy format
    return Object.entries(groups).map(([name, versions]) => {
      // Sort by effective date (descending) to find the most recent active policy
      const sortedVersions = versions.sort((a, b) =>
        new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime()
      );

      // Find the active policy (most recent with effective date <= now)
      const now = new Date();
      const activePolicy = sortedVersions.find(policy =>
        new Date(policy.effectiveDate) <= now
      ) || sortedVersions[0]; // fallback to most recent if none are effective yet

      return {
        name,
        activePolicy: activePolicy!,
        allVersions: sortedVersions,
        isExpanded: false
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const handleCreatePolicy = async () => {
    try {
      await adminApi.createConsentPolicy({
        name: formData.name,
        version: '1.0',
        policyText: formData.policyText,
        effectiveDate: formData.effectiveDate
      });
      await fetchPolicies();
      setIsCreateDialogOpen(false);
      setFormData({
        name: '',
        policyText: '',
        effectiveDate: new Date().toISOString().split('T')[0] ?? '',
        isActive: true
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create policy');
    }
  };

  const handleActivatePolicy = async (policyId: string) => {
    try {
      setActivatingPolicy(policyId);
      await adminApi.activateConsentPolicy(policyId);
      await fetchPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to activate policy');
    } finally {
      setActivatingPolicy(null);
    }
  };

  const toggleGroupExpansion = (groupName: string) => {
    setGroupedPolicies(prev =>
      prev.map(group =>
        group.name === groupName
          ? { ...group, isExpanded: !group.isExpanded }
          : group
      )
    );
  };

  const isActive = (policy: ConsentPolicy): boolean => {
    const now = new Date();
    return new Date(policy.effectiveDate) <= now;
  };

  const isMostRecentActive = (policy: ConsentPolicy, group: GroupedPolicy): boolean => {
    return policy.id === group.activePolicy.id;
  };

  const handleUpdatePolicy = async (policy: ConsentPolicy) => {
    try {
      const updatedData = {
        ...formData,
        version: (parseFloat(policy.version) + 0.1).toFixed(1) // Increment version
      };

      await adminApi.createConsentPolicy({
        name: updatedData.name,
        version: updatedData.version.toString(),
        policyText: updatedData.policyText,
        effectiveDate: updatedData.effectiveDate
      });

      await fetchPolicies();
      setEditingPolicy(null);
      setFormData({
        name: '',
        policyText: '',
        effectiveDate: new Date().toISOString().split('T')[0] ?? '',
        isActive: true
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update policy');
    }
  };

  const openEditDialog = (policy: ConsentPolicy) => {
    setEditingPolicy(policy);
    setFormData({
      name: policy.name,
      policyText: policy.policyText,
      effectiveDate: policy.effectiveDate.split('T')[0] ?? '',
      isActive: true
    });
  };

  const createResearchPolicy = async () => {
    try {
      await adminApi.createConsentPolicy({
        name: 'research_sharing',
        version: '1.0',
        policyText: `Research Data Sharing Consent

By agreeing to this policy, you consent to:

1. **Data Sharing**: Your conversation with our Research Agent will be shared with Ponder administrators and authorized research team members.

2. **Research Purposes**: This data will be used for:
   - Improving our AI conversation systems
   - Understanding user interaction patterns
   - Developing better user experience features
   - Academic research and publication (with personal identifiers removed)

3. **Data Protection**: We will:
   - Keep your data secure and confidential
   - Remove personal identifiers before any publication
   - Limit access to authorized personnel only
   - Retain data only as long as necessary for research purposes

4. **Your Rights**: You can:
   - Withdraw consent at any time during the conversation
   - Request deletion of your research data
   - Ask questions about how your data is used

By proceeding, you acknowledge that you understand and agree to these terms.`,
        effectiveDate: new Date().toISOString().split('T')[0]
      });
      await fetchPolicies();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create research policy');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">Loading consent policies...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Consent Policies</h1>
        <div className="flex gap-2">
          <Button onClick={createResearchPolicy} variant="outline">
            Create Research Policy
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                New Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Consent Policy</DialogTitle>
              </DialogHeader>
              <PolicyForm
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleCreatePolicy}
                submitLabel="Create Policy"
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-red-800">{error}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Active Policies</CardTitle>
        </CardHeader>
        <CardContent>
          {groupedPolicies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No consent policies found. Create your first policy to get started.
            </div>
          ) : (
            <div className="space-y-4">
              {groupedPolicies.map((group) => (
                <div key={group.name} className="border rounded-lg">
                  {/* Active Policy Header */}
                  <div className="p-4 bg-gray-50 border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleGroupExpansion(group.name)}
                          className="flex items-center gap-2 hover:bg-gray-100 p-1 rounded"
                        >
                          {group.isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <h3 className="font-medium text-lg">{group.name}</h3>
                        </button>
                        <Badge variant="default">
                          v{group.activePolicy.version} (Active)
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {new Date(group.activePolicy.effectiveDate).toLocaleDateString()}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(group.activePolicy)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground font-mono">
                      Hash: {group.activePolicy.policyHash ? group.activePolicy.policyHash.substring(0, 16) + '...' : 'N/A'}
                    </div>
                  </div>

                  {/* Expanded Versions */}
                  {group.isExpanded && (
                    <div className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-0">
                            <TableHead>Version</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Effective Date</TableHead>
                            <TableHead>Hash</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.allVersions.map((policy) => (
                            <TableRow key={policy.id} className={isMostRecentActive(policy, group) ? 'bg-green-50' : ''}>
                              <TableCell>{policy.version}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant={isMostRecentActive(policy, group) ? 'default' :
                                            isActive(policy) ? 'secondary' : 'outline'}
                                  >
                                    {isMostRecentActive(policy, group) ? 'Active' :
                                     isActive(policy) ? 'Superseded' : 'Future'}
                                  </Badge>
                                  {!isMostRecentActive(policy, group) && isActive(policy) && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleActivatePolicy(policy.id)}
                                      disabled={activatingPolicy === policy.id}
                                    >
                                      <Play className="w-3 h-3 mr-1" />
                                      {activatingPolicy === policy.id ? 'Activating...' : 'Activate'}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(policy.effectiveDate).toLocaleDateString()}
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-xs">
                                {policy.policyHash ? policy.policyHash.substring(0, 8) + '...' : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditDialog(policy)}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Policy Dialog */}
      <Dialog open={!!editingPolicy} onOpenChange={() => setEditingPolicy(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Policy: {editingPolicy?.name}</DialogTitle>
          </DialogHeader>
          {editingPolicy && (
            <PolicyForm
              formData={formData}
              setFormData={setFormData}
              onSubmit={() => handleUpdatePolicy(editingPolicy)}
              submitLabel="Update Policy (New Version)"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface PolicyFormProps {
  formData: PolicyFormData;
  setFormData: React.Dispatch<React.SetStateAction<PolicyFormData>>;
  onSubmit: () => void;
  submitLabel: string;
}

function PolicyForm({ formData, setFormData, onSubmit, submitLabel }: PolicyFormProps) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="name">Policy Name</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., research_sharing, data_processing"
        />
      </div>

      <div>
        <Label htmlFor="policyText">Policy Text</Label>
        <Textarea
          id="policyText"
          value={formData.policyText}
          onChange={(e) => setFormData(prev => ({ ...prev, policyText: e.target.value }))}
          rows={12}
          placeholder="Enter the full policy text that users will see..."
        />
      </div>

      <div>
        <Label htmlFor="effectiveDate">Effective Date</Label>
        <Input
          id="effectiveDate"
          type="date"
          value={formData.effectiveDate}
          onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
        />
      </div>

      <Button onClick={onSubmit} className="w-full">
        {submitLabel}
      </Button>
    </div>
  );
}